import { useState, useContext } from "react";
import { Card, Heading, Progress } from "@aidron/aidron-ds";
import { forecastColumns } from "../../../../../utils/tableColumns";
import { getMonthName } from "../../../../../utils/utils";

import { TenantContext } from "../../../../../templates/context/TenantContext";
import { useForecastStore } from "../../../../../viewModel/stores/forecastStore";

import { PredictionsColumnLabelsRow, PredictionRows } from "./predictionsTable.index";

import { TableControls } from "../../../../../components/SharedComponents/sharedComponents.index";

import { ColumnTitlesRow, TableColumnTitle } from "../../../sharedComponents/analysisModuleSharedComponents.index"

import PropTypes from 'prop-types';

import { useShallow } from "zustand/react/shallow";


export function PredictionsTable({ actions, ctx, uiState }) {

  const { setCheckedProducts } = actions || {};

  const { checkedProducts, selectedHierarchyIndexes, selectedProductIds, products } = ctx || {};

  const { freezeColumns } = uiState|| {};

  const { hierarchy, userInfo } = useContext(TenantContext);

  const {
    latestForecast,
    initialSortBy: forecastInitialSortBy,
    historicPredictionsToUse
  } = useForecastStore(
    useShallow((state) => ({
      latestForecast: state.latestForecast,
      initialSortBy: state.initialSortBy,
      historicPredictionsToUse: state.historicPredictionsToUse
    }))
  );

  const { predictions = [], monthColumns = [] } = latestForecast || {};

  const [checkColumnWidth, setCheckColumnWidth] = useState(0);
  const [hierarchyColumnsWidth, setHierarchyColumnsWidth] = useState([]);
  const [monthHeaderCellHeight, setMonthHeaderCellHeight] = useState(0);
  const [hierarchyCellHeight, setHierarchyCellHeight] = useState(0);
  const [sortRowsBy, setSortRowsBy] = useState(forecastInitialSortBy);

  const headerHeight = monthHeaderCellHeight + hierarchyCellHeight;

  const defineHierarchyToDisplay = () => hierarchy.levels.filter((level, index) =>
    selectedHierarchyIndexes.includes(index)
  );

  const evaluateLastCategoryColumn = (index) => freezeColumns && index === defineHierarchyToDisplay().length - 1 ? "right-border-fade" : "";

  const sumPreviousColumnsWidth = (currentColumnIndex, columnsWidth) => {
    const widthsToSum = columnsWidth ?? [checkColumnWidth].concat(hierarchyColumnsWidth);
    let sum = 0;
    for (let i = 0; i < currentColumnIndex; i++) {
      sum += widthsToSum[i];
    }
    return sum;
  };

  const generateColumnTitle = (data) => {
    if (data.label) return data.label;
    return `${getMonthName(data.month.toString())} ${data.year}`
  };

  if (!predictions?.length) {
    return (
      <div className="d-flex justify-content-center align-items-center width-40px-on-progress">
        <Progress circular />
      </div>
    );
  }

  if (!selectedProductIds.length) {
    return (
      <Card>
        <Heading size="sm">Sua busca não retornou resultados.</Heading>
      </Card>
    );
  }

  return (
    <TableControls freezeColumns={freezeColumns} plavVsForecastTableScrollBehavior={true}>
      <table id="download-table" className="table table-striped table-hover padding-070rem-on-th-and-td">
        <thead>
          <ColumnTitlesRow
            actions={{ setMonthHeaderCellHeight: setMonthHeaderCellHeight }}
            ctx={{ monthColumns: monthColumns, defineHierarchyToDisplay: defineHierarchyToDisplay }}
            uiState={{
              freezeColumns: freezeColumns,
              monthHeaderCellsSpan: forecastColumns().length,
              showSalesHistory: true,
              monthHeaderCellHeight: monthHeaderCellHeight,
              renderColumnTitleLabel: (monthColumns) => ( <TableColumnTitle label={generateColumnTitle(monthColumns)} /> )
            }}/>

          <PredictionsColumnLabelsRow
            actions={{ setSortRowsBy: setSortRowsBy, setHierarchyCellHeight: setHierarchyCellHeight }}
            ctx={{ 
              defineHierarchyToDisplay: defineHierarchyToDisplay, 
              monthColumns: monthColumns,
              historicPredictionsToUse: historicPredictionsToUse
            }}
            uiState={{
              freezeColumns: freezeColumns,
              sumPreviousColumnsWidth: sumPreviousColumnsWidth,
              evaluateLastCategoryColumn: evaluateLastCategoryColumn,
              monthHeaderCellHeight: monthHeaderCellHeight,
              sortRowsBy: sortRowsBy,
              futureColumns: forecastColumns
            }}/>
        </thead>
        <tbody>
          <PredictionRows
            actions={{
              setCheckColumnWidth: setCheckColumnWidth,
              setHierarchyColumnsWidth: setHierarchyColumnsWidth,
              setCheckedProducts: setCheckedProducts
            }}
            ctx={{
              checkedProducts: checkedProducts,
              selectedProductIds: selectedProductIds,
              selectedHierarchyIndexes: selectedHierarchyIndexes,
              historicPredictionsToUse: historicPredictionsToUse,
              products: products
            }}
            uiState={{
              freezeColumns: freezeColumns,
              sumPreviousColumnsWidth: sumPreviousColumnsWidth,
              evaluateLastCategoryColumn: evaluateLastCategoryColumn,
              headerHeight: headerHeight,
              sortRowsBy: sortRowsBy,
              defineHierarchyToDisplay: defineHierarchyToDisplay
            }}/>
        </tbody>
      </table>
    </TableControls>
  );
}

PredictionsTable.propTypes = {
  actions: PropTypes.shape(),
  ctx: PropTypes.shape(),
  uiState: PropTypes.shape()
};